// Copyright 2026, University of Maryland, All Rights Reserved
import { useEffect, useMemo, useState, type FC } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react'

import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCard,
  TableCardEmpty,
  TableCardLoading,
  Textarea,
} from '@/components/ui'
import { toast } from 'sonner'
import { ConfirmDialog, Field, FormDialog } from '@/components/forms'
import { apiClient } from '@/app-shell/api/api-client'
import type {
  KbSchema,
  KbSchemaField,
  KbSchemaFieldType,
} from '@/subsystems/knowledge-base/shared/types'

export const KB_SCHEMAS_QUERY_KEY = ['kb-schemas'] as const
export const KB_SCHEMAS_API_PATH = '/knowledge_base/schemas'

const FIELD_TYPES: KbSchemaFieldType[] = ['text', 'textarea', 'markdown']

const emptyField = (): KbSchemaField => ({
  key: '',
  label: '',
  type: 'textarea',
  required: false,
  description: '',
})

export const SchemasTab: FC = () => {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<KbSchema | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [deleting, setDeleting] = useState<KbSchema | null>(null)

  const { data: schemas = [], isLoading } = useQuery<KbSchema[]>({
    queryKey: KB_SCHEMAS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<KbSchema[]>(KB_SCHEMAS_API_PATH)
      return data ?? []
    },
    staleTime: 60_000,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: KB_SCHEMAS_QUERY_KEY })

  const deleteMutation = useMutation({
    mutationFn: async (schemaId: string) => apiClient.delete(`${KB_SCHEMAS_API_PATH}/${encodeURIComponent(schemaId)}`),
    onSuccess: () => {
      toast.success('KB schema deleted')
      refresh()
    },
    onError: (err: unknown) => {
      toast.error('Failed to delete', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12.5px] text-slate-500">
          Item schemas define the form fields used to author structured KB items (e.g.,
          negotiation criteria). They are unrelated to the extraction schemas on the Schemas page.
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          New Item Schema
        </Button>
      </div>

      <TableCard
        title="Item schemas"
        meta={
          schemas.length > 0 ? `${schemas.length} schema${schemas.length === 1 ? '' : 's'}` : undefined
        }
      >
        {isLoading ? (
          <TableCardLoading label="Loading item schemas…" />
        ) : schemas.length === 0 ? (
          <TableCardEmpty
            title="No item schemas yet"
            description="Create one to define the form fields authors fill in when adding items to a knowledge base."
            action={
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                New Item Schema
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Schema ID</TableHead>
                <TableHead>Fields</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[160px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {schemas.map(schema => (
                <TableRow key={schema.schemaId}>
                  <TableCell className="font-[550]">{schema.label}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{schema.schemaId}</TableCell>
                  <TableCell className="text-slate-600">
                    {schema.fields.map(f => f.key).join(', ')}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {schema.updatedAt ? new Date(schema.updatedAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(schema)}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleting(schema)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableCard>

      <SchemaFormDialog
        mode="create"
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={() => {
          refresh()
          toast.success('KB schema created')
        }}
      />
      <SchemaFormDialog
        mode="edit"
        open={!!editing}
        onOpenChange={open => !open && setEditing(null)}
        initial={editing ?? undefined}
        onSaved={() => {
          refresh()
          setEditing(null)
          toast.success('KB schema updated')
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
        title="Delete KB schema?"
        description={
          deleting
            ? `Permanently delete the KB schema "${deleting.label}" (${deleting.schemaId})? KB instances still referencing this schema will lose their field-meaning hints.`
            : ''
        }
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={async () => {
          if (!deleting) return
          await deleteMutation.mutateAsync(deleting.schemaId)
          setDeleting(null)
        }}
      />
    </div>
  )
}

interface SchemaFormDialogProps {
  mode: 'create' | 'edit'
  open: boolean
  onOpenChange: (open: boolean) => void
  initial?: KbSchema
  onSaved: () => void
}

const SchemaFormDialog: FC<SchemaFormDialogProps> = ({ mode, open, onOpenChange, initial, onSaved }) => {
  const [schemaId, setSchemaId] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [fields, setFields] = useState<KbSchemaField[]>([emptyField()])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setSchemaId(initial?.schemaId ?? '')
    setLabel(initial?.label ?? '')
    setDescription(initial?.description ?? '')
    setFields(
      initial?.fields?.length
        ? initial.fields.map(f => ({ ...f, description: f.description ?? '' }))
        : [emptyField()],
    )
  }, [open, initial])

  const canSubmit = useMemo(() => {
    if (submitting) return false
    if (!schemaId.trim() || !label.trim()) return false
    if (fields.length === 0) return false
    return fields.every(f => f.key.trim().length > 0 && f.label.trim().length > 0)
  }, [submitting, schemaId, label, fields])

  const updateField = (index: number, patch: Partial<KbSchemaField>) => {
    setFields(curr => curr.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  const moveField = (index: number, delta: -1 | 1) => {
    setFields(curr => {
      const next = [...curr]
      const target = index + delta
      if (target < 0 || target >= next.length) return curr
      const tmp = next[index]
      next[index] = next[target]
      next[target] = tmp
      return next
    })
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const payload: KbSchema = {
        schemaId: schemaId.trim(),
        label: label.trim(),
        description: description.trim() || null,
        fields: fields.map(f => ({
          key: f.key.trim(),
          label: f.label.trim(),
          type: f.type,
          required: f.required,
          description: (f.description ?? '').trim() || null,
        })),
      }
      if (mode === 'create') {
        await apiClient.post(KB_SCHEMAS_API_PATH, payload)
      } else {
        await apiClient.put(`${KB_SCHEMAS_API_PATH}/${encodeURIComponent(payload.schemaId)}`, payload)
      }
      onOpenChange(false)
      onSaved()
    } catch (err) {
      toast.error(mode === 'create' ? 'Failed to create' : 'Failed to update', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'create' ? 'New KB Schema' : `Edit KB Schema`}
      onSubmit={handleSubmit}
      submitting={submitting}
      canSubmit={canSubmit}
      submitLabel={mode === 'create' ? 'Create' : 'Save'}
      size="2xl"
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Schema ID" htmlFor="kbs-id">
          <Input
            id="kbs-id"
            value={schemaId}
            onChange={e => setSchemaId(e.target.value)}
            placeholder="e.g. negotiation-item"
            disabled={mode === 'edit'}
          />
        </Field>
        <Field label="Label" htmlFor="kbs-label">
          <Input
            id="kbs-label"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Display name"
          />
        </Field>
      </div>
      <Field label="Description" htmlFor="kbs-description">
        <Textarea
          id="kbs-description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What this schema is used for"
          rows={2}
        />
      </Field>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Fields</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setFields(curr => [...curr, emptyField()])}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add field
          </Button>
        </div>
        <div className="space-y-3">
          {fields.map((f, index) => (
            <div key={index} className="rounded-md border border-border p-3 space-y-2">
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-3">
                  <Field label="Key">
                    <Input
                      value={f.key}
                      onChange={e => updateField(index, { key: e.target.value })}
                      placeholder="preferred"
                    />
                  </Field>
                </div>
                <div className="col-span-3">
                  <Field label="Label">
                    <Input
                      value={f.label}
                      onChange={e => updateField(index, { label: e.target.value })}
                      placeholder="Preferred"
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label="Type">
                    <Select
                      value={f.type}
                      onValueChange={value => updateField(index, { type: value as KbSchemaFieldType })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(t => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="col-span-2 flex items-end gap-2 pb-1">
                  <Checkbox
                    id={`kbs-required-${index}`}
                    checked={f.required}
                    onCheckedChange={value => updateField(index, { required: value === true })}
                  />
                  <label htmlFor={`kbs-required-${index}`} className="text-sm">
                    Required
                  </label>
                </div>
                <div className="col-span-2 flex items-end justify-end gap-1 pb-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => moveField(index, -1)}
                    disabled={index === 0}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => moveField(index, 1)}
                    disabled={index === fields.length - 1}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setFields(curr => curr.filter((_, i) => i !== index))}
                    disabled={fields.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Field
                label="Description"
                helper="Injected into the LLM prompt above the KB items so the model knows what this field means."
              >
                <Textarea
                  value={f.description ?? ''}
                  onChange={e => updateField(index, { description: e.target.value })}
                  placeholder="Meaning of this field"
                  rows={2}
                />
              </Field>
            </div>
          ))}
        </div>
      </div>
    </FormDialog>
  )
}
